import type { Organization } from "../../lib/api-types.js";

/**
 * 组织搭建 wizard 每一步的统一契约。
 * 步骤组件在「本步达成可进入下一步的条件」时调用 onReadyChange(true)。
 * 容器据此点亮「下一步」；点下一步时由容器统一 PATCH completeStep。
 */
export interface OnboardingStepProps {
  org: Organization;
  /** 该步是否已记入 completedSteps（revisit 时初始即就绪） */
  alreadyCompleted: boolean;
  /** 步骤内部就绪态变化回调 */
  onReadyChange: (ready: boolean) => void;
}

export interface OnboardingStepMeta {
  step: number;
  /** stepper 左栏标题 */
  navTitle: string;
  /** stepper 左栏副标题 */
  navSubtitle: string;
  /** 右侧内容区大标题 */
  heading: string;
  /** 右侧内容区一句话引导（面向不懂技术的管理者） */
  intro: string;
  /** 「下一步」未就绪时的提示，告诉用户还差什么 */
  blockedHint: string;
}

export const ONBOARDING_STEPS: ReadonlyArray<OnboardingStepMeta> = [
  {
    step: 1,
    navTitle: "关联飞书",
    navSubtitle: "连接企业通讯录",
    heading: "连接你们的飞书",
    intro:
      "DOSIA 通过飞书认识你们公司的每一位同事。填入飞书自建应用的凭据并建立连接，后面几步都基于它自动完成。",
    blockedHint: "请先完成飞书连接（状态为「已连接」且已同步到部门）。",
  },
  {
    step: 2,
    navTitle: "同步组织架构",
    navSubtitle: "拉取部门与成员",
    heading: "确认组织架构",
    intro:
      "把飞书里的部门和成员同步进来。展开任意部门即可看到其中的同事，标「组长」的是飞书里的部门负责人。",
    blockedHint: "请先同步一次组织架构，确保至少有一个部门。",
  },
  {
    step: 3,
    navTitle: "生成角色",
    navSubtitle: "AI 推荐职能角色",
    heading: "为团队生成角色",
    intro:
      "DOSIA 会根据你们的部门结构，智能推荐一套职能角色（如内容、媒介、行政）。你可以逐条修改后再确认应用。",
    blockedHint: "请生成并「确认应用」一套角色后再继续。",
  },
  {
    step: 4,
    navTitle: "关联部门与负责人",
    navSubtitle: "角色 ↔ 部门 ↔ 负责人",
    heading: "把角色和团队对上号",
    intro:
      "为每个角色指定它对应的飞书部门，以及这个角色的负责人。负责人将作为汇报与审批对象。",
    blockedHint: "请为每个角色关联至少一个部门和一位负责人。",
  },
  {
    step: 5,
    navTitle: "设置管理员",
    navSubtitle: "指定管理员并完成",
    heading: "指定管理员，完成搭建",
    intro:
      "最后，选出 1~2 位管理员（通常是你自己或核心负责人）。确认无误后，组织底座就搭建完成了。",
    blockedHint: "请至少指定一位管理员。",
  },
];
